import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip, Grid, TextField, Typography, Box, Button, Checkbox } from '@material-ui/core';
import { AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoOutlineIcon from '@material-ui/icons/InfoOutlined';

import GuideIcon from '@material-ui/icons/MenuBookSharp';

import { withAuthenticationRequired } from '@auth0/auth0-react';
import mixpanel from 'mixpanel-browser';

import { logAuthenticationRequired } from '../utils/authLib';
import { useAxios } from '../utils/axiosApi';
import { onError } from '../utils/errorLib';
import { collectTags } from '../utils/materialsLib';
import { AppContext } from '../components/AppState';
import { useUser } from '../utils/users';
import { StyledAccordion, StyledAccordionSummary } from '../components/Accordions';
import GalleryTabSelector from '../components/GalleryTabSelector';
import PhotoUpload, { UploadMethod, UploadMethodNames } from '../components/PhotoUpload';
import ProgressButton from '../components/ProgressButton';
import TagList from '../components/TagList';
import { importLandingPage } from '../utils/shared/business';
import { PseudoTextureTypes } from '../utils/shared/formatLib';
import { ScanType } from '../utils/shared/metadataLib';
import { importGuideUrl } from '../utils/linksLib';
import { useAuth } from '../utils/useAuth';


const tagsHelpText = 'Tagging your materials can be useful for filtering and finding materials in your library'

const scanType = ScanType.TEXTURE_IMPORT;

const useStyles = makeStyles((theme) => ({
  form: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
    },
  },
  tagsContainer: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(2.5),
  },
  tagsText: {
    minWidth: '40px',
  },
  tags: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  guideButton: {
    marginTop: theme.spacing(-1),
    marginLeft: theme.spacing(2.5),
  },
  guideIcon: {
    marginRight: theme.spacing(0.5),
  },
  helpIcon: {
    marginLeft: theme.spacing(0.5),
    opacity: 0.5,
  },
  discountText: {
    color: theme.palette.primary.main,
    fontSize: '0.95em',
  },
  discountIcon: {
    color: theme.palette.primary.main,
    fontSize: '2em',
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.25),
  },
  accordion: {
    marginTop: theme.spacing(1),
    marginLeft: '0.5em',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
}));

function Import(props) {
  const router = useRouter();
  const { galleryUser, showAlert, setNavWarning, userTags, qaDisplay, importAvailable } = useContext(AppContext);

  const { apiPost } = useAxios();

  const [tagList, setTagList] = useState([]);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [sku, setSku] = useState('');
  const [photos, setPhotos] = useState([]);
  const [originalPhotos, setOriginalPhotos] = useState([]);
  // We use a Map inside an object to trigger a re-render when the Map is updated
  // https://stackoverflow.com/a/78188977/22374074
  const [importFilenames, setImportFilenames] = useState({ items: new Map() });
  const [uploadComplete, setUploadComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processSuccess, setProcessSuccess] = useState(false);
  const [processFail, setProcessFail] = useState(false);
  const [redirect, setRedirect] = useState(null);
  const [materialId, setMaterialId] = useState();
  const [uploadMethod, setUploadMethod] = useState(UploadMethod.UPPY);
  const [uploadLinks, setUploadLinks] = useState({
    [UploadMethod.UPPY]: '',
    [UploadMethod.WE_TRANSFER]: '',
    [UploadMethod.S3]: 's3://',
  });
  const [skipTiling, setSkipTiling] = useState(false);
  const [advancedProcessingExpanded, setAdvancedProcessingExpanded] = useState(false);

  const { isAuthenticated } = useAuth();
  const { userItem: galleryUserItem } = useUser(galleryUser);

  const classes = useStyles();

  useEffect(() => {
    mixpanel.track('View Import');
  }, []);

  useEffect(() => {
    const disallowImport = (importAvailable === false) && !qaDisplay;
    if (disallowImport) {
      window.open(importLandingPage, '_blank');
      setRedirect('/home');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importAvailable]);

  function updateAdvancedProcessing(event, expanded) {
    setAdvancedProcessingExpanded(expanded);
  }

  function updateImportFilenames(filename, mapType) {
    setImportFilenames(({ items }) => ({ items: items.set(filename, mapType) }))
  }

  let mapTypeErrors = [];
  const duplicateRealMapTypes = Array.from(importFilenames.items.values()).filter(
    (value, index, self) => self.indexOf(value) !== index && value !== ''
  );
  if (duplicateRealMapTypes.length > 0) {
    mapTypeErrors.push('Duplicate texture types: ' + Array.from(new Set(duplicateRealMapTypes.sort())).join(', '));
  }
  let duplicatePseudoMapTypes = [];
  for (const value of importFilenames.items.values()) {
    if (
      PseudoTextureTypes.has(value) &&
      Array.from(importFilenames.items.values()).includes(PseudoTextureTypes.get(value))
    ) {
      duplicatePseudoMapTypes.push(value);
      duplicatePseudoMapTypes.push(PseudoTextureTypes.get(value));
      const errorMessage = `Conflicting texture types: ${value} and ${PseudoTextureTypes.get(value)}`;
      if (!mapTypeErrors.includes(errorMessage)) {
        mapTypeErrors.push(errorMessage);
      }
    }
  }
  const duplicateMapTypes = [...new Set([...duplicateRealMapTypes, ...duplicatePseudoMapTypes])];
  if (!Array.from(importFilenames.items.values()).every((value) => Boolean(value))) {
    mapTypeErrors.push('All filenames need a texture type (set to basecolor if unsure)')
  }

  function validateForm() {
    return (
      uploadComplete &&
      !loading &&
      materialId &&
      (uploadMethod !== UploadMethod.UPPY || (
        importFilenames.items.size > 0 &&
        mapTypeErrors.length === 0 &&
        originalPhotos.every(op => Boolean(importFilenames.items.get(op.name)))
      ))
    );
  }

  function tagNamesFromList(tagList) {
    const tagNames = [];
    if (tagList) {
      tagList.forEach((t) => {
        tagNames.push(t.label);
      });
    }
    return tagNames;
  }

  async function onTagsChanged(materialId, tagsChanged) {
    const { selected, _secondary } = tagsChanged;
    setTagList(collectTags(null, false, selected));
  }

  async function submitUppy() {
    let success = false;

    const types = photos.map((photo, index) => {
      const originalFilename = originalPhotos[index].name;
      const textureType = importFilenames.items.get(originalFilename);
      return textureType;
    });

    // TODO: Fill in constant texture image values if supplied by the user.

    const scanConfig = {
      types,
    };

    const scan = {
      photos,
      originalPhotos,
      name,
      text,
      sku,
      scanConfig,
      skipTiling,
    };

    if (tagList) {
      scan['tags'] = tagNamesFromList(tagList);
    }

    var createResult = {};
    try {
      createResult = await createScan(galleryUser, materialId, scan);
    } catch (e) {
      onError(e);
    }

    if (createResult.status === 'success') {
      const { materialUserPath } = createResult.data;

      var processResult = {};
      try {
        processResult = await processScan(galleryUser, materialId, materialUserPath);
      } catch (e) {
        onError(e);
      }

      success = (processResult.status === 'success');
    }
    return success;
  }

  async function submitUploadLink() {
    let success = false;
    let data;
    let response;

    try {
      response = await apiPost('/batch/upload',
        {
          userId: galleryUserItem.userId,
          sourceUrl: uploadLinks[uploadMethod],
          tags: tagNamesFromList(tagList),
          process: true,
          scanType,
          skipTiling,
        },
      );
      success = (response?.data?.status === 'success');
      data = response?.data;
    } catch(e) {
      onError(e);
    }

    return [success, data];
  }

  async function submit(event) {
    event.preventDefault();
    mixpanel.track('Import Texture', {
      'Material Name': name,
      'Description': text,
      'SKU': sku,
      'Texture Count': importFilenames.items.size,
      'Upload Link': uploadLinks[uploadMethod],
      'Screen': 'Import',
      'Method': UploadMethodNames[uploadMethod],
    });

    let uploadSuccess = false;
    let uploadData;
    setNavWarning(false);
    setLoading(true);

    if (uploadMethod === UploadMethod.UPPY) {
      uploadSuccess = await submitUppy();
    } else if ([UploadMethod.WE_TRANSFER, UploadMethod.S3].includes(uploadMethod)) {
      [uploadSuccess, uploadData] = await submitUploadLink();
    }

    setLoading(false);

    if (uploadSuccess) {
      setProcessSuccess(true);
      showAlert({ status: 'success', message: 'Texture processing launched' });
    } else {
      setProcessFail(true);
      let reason = '';
      if (uploadData && 'sourceUrl' in uploadData?.data) {
        reason = `: ${uploadData?.data?.sourceUrl}`;
      }
      showAlert({ status: 'error', message: `Error launching texture processing${reason}` });
    }
    setRedirect('/status');
  }

  // Create a new scan material in the database
  async function createScan(userId, materialId, scan) {
    // Exception handling is performed by the caller
    //console.log('createScan:', scan);

    const response = await apiPost('/materials', {
        userId,
        materialId,
        scanType,
        originMethod: 'upload',
        ...scan
    });
    return response.data;
  }

  // Start processing a scan
  async function processScan(userId, materialId, materialUserPath) {
    // Exception handling is performed by the caller
    console.log(`Processing materialId ${materialId} for userId ${userId} using materialUserPath ${materialUserPath}`);

    const response = await apiPost('/process',
      { materialUserPath, action: 'texin', target: ['shimmer', 'textures', 'tiled'] },
      {
        params: { userId, materialId },
      }
    );
    return response.data;
  }

  if (!isAuthenticated || !galleryUser) {
    return null;
  }

  if (redirect) {
    router.push(redirect);
  }

  const allowImport = importAvailable || qaDisplay;

  return (
    <>
      <Head>
        <title>Bandicoot - App - Import</title>
      </Head>

      <GalleryTabSelector current='import' />

      {/*
        TODO: Finalise content and enable by disabling earlier redirects.
        Or, ignore content by leaving earlier redirects in place
      */}
      {!allowImport && (<>
        <Typography>
          Be one of the first to experience the tiling revolution of effortless PBR tiling.
        </Typography>
        <Typography>
          <a href={importLandingPage} target='_blank'>
            Sign up here
          </a>
        </Typography>
      </>)}
      {allowImport && (<>
        <Box maxWidth='100em'>
          <Grid container alignContent='flex-start' alignItems='flex-start' spacing={2}>
            <Grid item>
              <Typography>
                Upload your PBR texture to create a seamlessly tiled 3D-ready digital material.
              </Typography>
            </Grid>
            <Grid item>
              <Button
                className={classes.guideButton}
                color='primary'
                onClick={() => window.open(importGuideUrl, '_blank')}
              >
                <GuideIcon className={classes.guideIcon} />
                Import guide
              </Button>
            </Grid>
          </Grid>

          <form className={classes.form} onSubmit={submit}>
            <Grid container spacing={2}>
              <Grid item xs={9}>
                <TextField
                  variant="filled"
                  fullWidth
                  margin="normal"
                  label="Material name"
                  onChange={e => setName(e.target.value)}
                  value={uploadMethod === UploadMethod.UPPY ? name : '(the texture folder names will be used as the material names)'}
                  required={uploadMethod === UploadMethod.UPPY}
                  disabled={uploadMethod !== UploadMethod.UPPY}
                  size="small"
                  />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  variant="filled"
                  fullWidth
                  margin="normal"
                  label="SKU"
                  onChange={e => setSku(e.target.value)}
                  value={uploadMethod === UploadMethod.UPPY ? sku : '(set the SKUs after processing)'}
                  disabled={uploadMethod !== UploadMethod.UPPY}
                  size="small"
                  />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={9}>
                <TextField
                  variant="filled"
                  fullWidth
                  margin="normal"
                  label="Description"
                  onChange={e => setText(e.target.value)}
                  value={uploadMethod === UploadMethod.UPPY ? text : '(set the descriptions after processing)'}
                  disabled={uploadMethod !== UploadMethod.UPPY}
                  size="small"
                />
              </Grid>
              <Grid item xs={3}>
                <Grid container className={classes.tagsContainer}>
                  <Grid item xs={2} className={classes.tagsText}>
                    <Tooltip title={tagsHelpText}>
                      <Typography variant='body1'>
                        Tags
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={10} className={classes.tags}>
                    <TagList
                      materialId={materialId}
                      tags={tagList}
                      onChange={onTagsChanged}
                      tagChoices={userTags}
                      alwaysExpanded={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <StyledAccordion
              className={classes.accordion}
              expanded={advancedProcessingExpanded}
              onChange={updateAdvancedProcessing}
            >
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />} >
                <Typography variant='caption'>Advanced settings</Typography>
              </StyledAccordionSummary>
              <AccordionDetails>
                <Grid container>
                  <Grid item xs={12}>
                    <div className={classes.checkboxContainer}>
                      <Checkbox
                        color="primary"
                        checked={skipTiling}
                        onChange={() => setSkipTiling(!skipTiling)}
                      />
                      <Typography
                        display='inline'
                        onClick={() => setSkipTiling(!skipTiling)}
                        style={{ cursor: 'pointer' }}
                      >
                        Disable tiling
                      </Typography>
                      <Tooltip
                        title={
                          'When tiling is disabled, the tiling process will be skipped. ' +
                          'This is useful when the texture is already seamlessly tiled or tiling is not desired, ' +
                          'for example when importing texture for stitching.'
                        }
                      >
                        <InfoOutlineIcon
                          className={classes.helpIcon}
                          fontSize='small'
                          color='secondary'
                        />
                      </Tooltip>
                    </div>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </StyledAccordion>

            <div>
              <PhotoUpload
                key={scanType}
                scanType={scanType}
                userId={galleryUser}
                userItem={galleryUserItem}
                name={name}
                setName={setName}
                setPhotos={setPhotos}
                originalPhotos={originalPhotos}
                setOriginalPhotos={setOriginalPhotos}
                importFilenames={importFilenames}
                setImportFilenames={setImportFilenames}
                updateImportFilenames={updateImportFilenames}
                setUploadComplete={setUploadComplete}
                duplicateMapTypes={duplicateMapTypes}
                mapTypeErrors={mapTypeErrors}
                materialId={materialId}
                setMaterialId={setMaterialId}
                onUploading={setNavWarning}
                uploadMethod={uploadMethod}
                setUploadMethod={setUploadMethod}
                uploadLinks={uploadLinks}
                setUploadLinks={setUploadLinks}
              />
            </div>
            <div>
              <ProgressButton
                size="large"
                type="submit"
                disabled={!validateForm()}
                loading={loading}
                success={processSuccess}
                tooltip={
                  validateForm() ? 'Click to begin import' :
                  'Please drag your texture images in and ensure each is assigned a unique texture type'
                }
              >
                {uploadMethod === UploadMethod.UPPY ? 'Import material' : 'Import materials'}
              </ProgressButton>
            </div>
          </form>
        </Box>
      </>)}
    </>
  );
}

export default withAuthenticationRequired(
  Import,
  { onBeforeAuthentication: () => { logAuthenticationRequired('Import') } }
);
